import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Download, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
    const [purchases, setPurchases] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchPurchases();
    }, []);

    const fetchPurchases = async () => {
        try {
            const response = await axios.get(`${API}/purchases`);
            setPurchases(response.data);
        } catch (error) {
            console.error('Error fetching purchases:', error);
            toast.error('Failed to load purchases');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center pt-16">
                <div className="text-xl text-muted-foreground">Loading...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen pt-24 px-6 md:px-12 lg:px-24 py-12" data-testid="dashboard-page">
            <h1 className="text-5xl md:text-7xl font-bold tracking-tighter mb-12">My Purchases</h1>

            {purchases.length === 0 ? (
                <div className="text-center py-24" data-testid="no-purchases">
                    <p className="text-xl text-muted-foreground mb-6">You haven't made any purchases yet</p>
                    <button 
                        onClick={() => window.location.href = '/browse'}
                        className="btn-primary"
                        data-testid="start-shopping-button"
                    >
                        Start Shopping
                    </button>
                </div>
            ) : (
                <div className="space-y-8" data-testid="purchases-list">
                    {purchases.map((purchase) => (
                        <div 
                            key={purchase.id}
                            className="bg-card border border-border p-6"
                            data-testid={`purchase-${purchase.id}`}
                        >
                            <div className="flex items-start justify-between mb-6">
                                <div>
                                    <div className="flex items-center gap-3 mb-2">
                                        <Calendar className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                                        <span className="text-sm text-muted-foreground">
                                            {formatDate(purchase.created_at)}
                                        </span>
                                    </div>
                                    <p className="text-2xl font-bold text-primary" data-testid={`purchase-total-${purchase.id}`}>
                                        ${purchase.total_amount}
                                    </p>
                                </div>
                                <span className="px-4 py-2 bg-green-500/10 text-green-500 text-xs font-mono uppercase tracking-wider">
                                    Completed
                                </span>
                            </div>

                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {purchase.products?.map((product) => (
                                    <div 
                                        key={product.id}
                                        className="flex gap-4 bg-secondary/50 p-4"
                                        data-testid={`purchased-product-${product.id}`}
                                    >
                                        <img 
                                            src={product.image_url} 
                                            alt={product.name}
                                            className="w-16 h-16 object-cover"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <h4 className="font-medium text-sm mb-1 truncate">{product.name}</h4>
                                            <p className="text-xs text-muted-foreground mb-2">${product.price}</p>
                                            <button 
                                                onClick={() => window.open(product.audio_url, '_blank')}
                                                className="flex items-center gap-2 text-xs text-primary hover:underline"
                                                data-testid={`download-${product.id}`}
                                            >
                                                <Download className="w-3 h-3" />
                                                Download
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Dashboard;
